// CBD (Commercial Bank of Dubai) — conventional Visa statement.
// Summary box: label@x≈260 → value@x≈400 on the same y-band.
// Transactions: rows led by a DD-MM-YYYY date at x<110; columns are
// trxn-date / posting-date / description(+location+country) / [orig] / AED.
import { toNum, dmyToISO, rows, fieldRight, fieldBelow, amountWithSign } from '../util.js';

export function parseCBD(pages) {
  const items = pages[0] || []; // summary box is always on page 1 (page 2+ = T&C)

  const summary = {
    statementDate: dmyToISO(fieldRight(items, 'Statement Date', { minX: 360, want: /\d{2}[-/]\d{2}/ })),
    dueDate: dmyToISO(fieldRight(items, 'Payment Due Date', { minX: 360, want: /\d{2}[-/]\d{2}/ })),
    minimumPayment: toNum(fieldRight(items, 'Minimum', { minX: 360 })),
    closingBalance: toNum(fieldRight(items, 'Total Amount Due', { minX: 360 })),
    creditLimit: toNum(fieldRight(items, 'Total Credit Limit', { minX: 360 })),
    availableLimit: toNum(fieldRight(items, 'Available Credit Limit', { minX: 360 })),
    // CBD labels the opening figure "Opening Balance" in the column-layout
    // "Summary (in AED)" box (value sits BELOW the label, not to its right).
    previousBalance: toNum(fieldBelow(items, 'Opening Balance')),
  };

  // Transactions: every page, rows whose first cell is a date near the left margin.
  const txns = [];
  for (const page of pages) {
    for (const { cells } of rows(page)) {
      if (!cells.length) continue;
      const first = cells[0];
      if (first.x > 120 || !/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(first.s)) continue;
      // Need a posting date as the 2nd date-ish cell and an amount as the rightmost numeric.
      const amountCell = [...cells].reverse().find((c) => /^[\d,]+\.\d{2}/.test(c.s));
      if (!amountCell) continue;
      const posting = cells[1] && /^\d{2}[-/]\d{2}[-/]\d{4}$/.test(cells[1].s) ? cells[1].s : null;
      // Description = cells between the dates and the amount (x in the middle band).
      const mid = cells.filter((c) => c.x > 180 && c.x < amountCell.x - 4 && c !== amountCell);
      // A foreign original shows as a "12.34]" token just before the amount.
      const origCell = mid.find((c) => /\]$/.test(c.s));
      const desc = mid.filter((c) => c !== origCell).map((c) => c.s).join(' ').replace(/\s{2,}/g, ' ').trim();
      const { amount, credit } = amountWithSign(amountCell.s);
      if (amount == null || !desc) continue;
      txns.push({
        date: dmyToISO(first.s),
        postingDate: posting ? dmyToISO(posting) : null,
        description: desc,
        amount,
        credit,                                   // payments / refunds / cashback
        originalAmount: origCell ? toNum(origCell.s) : null,
      });
    }
  }

  return { bank: 'CBD', summary, transactions: txns };
}
